import { useCallback, useMemo } from 'react'

import FileDownloadIcon from '@mui/icons-material/FileDownload'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Button, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { format } from 'date-fns'

import { BIDS_PER_JOB_ANALYSIS_CHART_HEIGHT } from 'components/reports/Metrics/constants'
import { DATE_FORMAT } from 'constants/DateFormats'

import { BidsPerJobAnalysisHistogram } from '../components/types'
import { BidsPerJobAnalysisChart } from './BidsPerJobAnalysisChart'

interface Props {
  histogram?: BidsPerJobAnalysisHistogram | null
  loading?: boolean
  start: Date | null
  end: Date | null
}

export const BidsPerJobAnalysisCard = ({ histogram, loading, start, end }: Props) => {
  const buckets = useMemo(() => histogram?.buckets ?? [], [histogram])

  const handleExportCsv = useCallback(() => {
    const header = 'Bids received,All Jobs,CX Won,OP Won'
    const rows = buckets.map(
      (bucket) =>
        `${bucket.label},${bucket.allJobsCount},${bucket.cxWonJobsCount},${bucket.opWonJobsCount}`,
    )
    const csv = [header, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    const startLabel = start ? format(start, DATE_FORMAT) : 'start'
    const endLabel = end ? format(end, DATE_FORMAT) : 'end'
    link.href = url
    link.download = `bids-per-job-analysis-${startLabel}-${endLabel}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }, [buckets, start, end])

  return (
    <Paper variant='outlined' sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
        mb={2}>
        <Box>
          <Typography variant='subtitle1' fontWeight={600}>
            Number of bids received per job
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Distribution of jobs sent for bidding by how many bids they received, split by which
            network won each job.
          </Typography>
        </Box>

        <Stack direction='row' spacing={2} alignItems='center'>
          <Stack alignItems='flex-end'>
            <Typography variant='caption' color='text.secondary'>
              Total jobs
            </Typography>
            {loading ? (
              <Skeleton width={60} height={32} />
            ) : (
              <Typography variant='h6' fontWeight={700}>
                {(histogram?.totalJobs ?? 0).toLocaleString()}
              </Typography>
            )}
          </Stack>

          <Button
            variant='outlined'
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCsv}
            disabled={buckets.length === 0}>
            Export CSV
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Skeleton
          variant='rectangular'
          height={BIDS_PER_JOB_ANALYSIS_CHART_HEIGHT}
          sx={{ borderRadius: 1 }}
        />
      ) : (
        <BidsPerJobAnalysisChart buckets={buckets} />
      )}

      <Stack
        direction='row'
        spacing={1}
        alignItems='flex-start'
        sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
        <Box sx={{ color: 'text.secondary', display: 'flex', pt: 0.25 }}>
          <InfoOutlinedIcon fontSize='small' />
        </Box>
        <Box>
          <Typography variant='caption' color='text.secondary' display='block'>
            Counts jobs sent for bidding (bidding enabled, or with at least one driver bid) that
            were created within the selected date range. Each job is grouped by its total number of
            driver bids, then split by whether a CX or Onepoint driver won the job; jobs with no
            winning bid recorded are only reflected in the &quot;All Jobs&quot; series.
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}
